import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, AlertTriangle, Wind } from 'lucide-react';
import { articleHtml } from '@/lib/articleBody';

const NEW_CFG = { enabled: false, voice: true, phases: [{ type: 'inhale', label: 'Inhale', seconds: 4 }, { type: 'exhale', label: 'Exhale', seconds: 6 }], academy_article_id: '', academy_section: '', review_note: '' };
const sel = 'h-9 rounded-md bg-transparent border border-[var(--c-hair)] px-2 text-sm';

const headingsOf = (a) => {
  if (!a) return [];
  const d = document.createElement('div'); d.innerHTML = articleHtml(a);
  return [...d.querySelectorAll('h1,h2,h3')].map(h => h.textContent.trim()).filter(Boolean);
};

export const BreathingConfigEditor = ({ value, onChange }) => {
  const [articles, setArticles] = useState([]);
  useEffect(() => { api.get('/articles').then(r => setArticles(r.data)).catch(() => {}); }, []);
  if (!value) return (
    <Button type="button" variant="outline" onClick={() => onChange(NEW_CFG)} data-testid="breathing-add-config" className="w-full border-[var(--c-hair)]">
      <Wind className="w-4 h-4 mr-2" /> Add guided breathing timer
    </Button>
  );
  const set = (patch) => onChange({ ...value, ...patch });
  const setPhase = (i, patch) => set({ phases: value.phases.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const heads = headingsOf(articles.find(a => a.id === value.academy_article_id));
  return (
    <div className="rounded-xl border border-[var(--c-hair)] p-3 space-y-3" data-testid="breathing-config">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm flex items-center gap-1.5"><Wind className="w-4 h-4 text-[#7DD3FC]" /> Guided breathing timer</div>
        <div className="flex items-center gap-2 text-xs">Timer on<Switch checked={value.enabled} onCheckedChange={v => set({ enabled: v })} data-testid="breathing-enabled" /></div>
      </div>
      {value.review_note && (
        <div className="rounded-lg bg-[#FFB800]/10 border border-[#FFB800]/30 p-2.5 text-xs flex gap-2" data-testid="breathing-review-note">
          <AlertTriangle className="w-4 h-4 text-[#FFB800] flex-shrink-0" /><span>{value.review_note} Turning the timer on and saving confirms these timings.</span>
        </div>
      )}
      <div className="space-y-2">
        <Label>Breathing phases (one cycle)</Label>
        {value.phases.map((p, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`breathing-phase-${i}`}>
            <select value={p.type} onChange={e => setPhase(i, { type: e.target.value })} className={sel} data-testid={`breathing-phase-type-${i}`}>
              <option value="inhale">Inhale</option><option value="hold">Hold</option><option value="exhale">Exhale</option>
            </select>
            <Input value={p.label} onChange={e => setPhase(i, { label: e.target.value })} placeholder="Spoken name" className="h-9 bg-transparent border-[var(--c-hair)]" data-testid={`breathing-phase-label-${i}`} />
            <Input type="number" min="0.5" step="0.5" value={p.seconds} onChange={e => setPhase(i, { seconds: e.target.value })} className="h-9 w-20 bg-transparent border-[var(--c-hair)] font-mono" data-testid={`breathing-phase-seconds-${i}`} />
            <span className="text-xs text-muted-foreground">s</span>
            <button type="button" onClick={() => set({ phases: value.phases.filter((_, j) => j !== i) })} className="p-1 text-red-400" aria-label="Remove phase"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <Button type="button" size="sm" variant="ghost" onClick={() => set({ phases: [...value.phases, { type: 'hold', label: 'Hold', seconds: 4 }] })} data-testid="breathing-add-phase"><Plus className="w-3.5 h-3.5 mr-1" /> Add phase</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Academy article</Label>
          <select value={value.academy_article_id || ''} onChange={e => set({ academy_article_id: e.target.value })} className={`${sel} w-full mt-1`} data-testid="breathing-article">
            <option value="">— None —</option>
            {articles.map(a => <option key={a.id} value={a.id}>{a.title}{a.published ? '' : ' (draft)'}</option>)}
          </select>
        </div>
        <div><Label>Article section</Label>
          <select value={value.academy_section || ''} onChange={e => set({ academy_section: e.target.value })} className={`${sel} w-full mt-1`} data-testid="breathing-section">
            <option value="">— Top of article —</option>
            {[...new Set([value.academy_section, ...heads].filter(Boolean))].map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm"><Switch checked={value.voice !== false} onCheckedChange={v => set({ voice: v })} data-testid="breathing-voice" /> Voice guidance available</div>
    </div>
  );
};
